import {
  createRouter,
  createRootRoute,
  createRoute,
} from '@tanstack/react-router';

import { RootLayout } from '@/routes/__root';
import { AuthedLayout } from '@/routes/_authed';
import { DashboardPage } from '@/routes/_authed/dashboard';
import { PipelinePage } from '@/routes/_authed/pipeline';
import { DealsIndexPage } from '@/routes/_authed/deals/index';
import { DealDetailPage } from '@/routes/_authed/deals/$dealId';
import { NewDealPage } from '@/routes/_authed/deals/new';
import { CommissionsIndexPage } from '@/routes/_authed/commissions/index';
import { CommissionCalculatorPage } from '@/routes/_authed/commissions/calculator';
import { CommissionPlansPage } from '@/routes/_authed/commissions/plans';
import { TeamIndexPage } from '@/routes/_authed/team/index';
import { TeamMemberPage } from '@/routes/_authed/team/$memberId';
import { TeamInvitePage } from '@/routes/_authed/team/invite';
import { SettingsIndexPage } from '@/routes/_authed/settings/index';
import { SettingsPipelinePage } from '@/routes/_authed/settings/pipeline';
import { SettingsIntegrationsPage } from '@/routes/_authed/settings/integrations';
import { SettingsBillingPage } from '@/routes/_authed/settings/billing';
import { ReportsIndexPage } from '@/routes/_authed/reports/index';
import { LeaderboardPage } from '@/routes/_authed/reports/leaderboard';

// Root route
const rootRoute = createRootRoute({
  component: RootLayout,
});

// Authed layout — CF Access protects the entire app at the edge
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  component: AuthedLayout,
});

// Authed child routes
const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/pipeline',
  component: PipelinePage,
});

const dealsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/deals',
  component: DealsIndexPage,
});

const dealDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/deals/$dealId',
  component: DealDetailPage,
});

const newDealRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/deals/new',
  component: NewDealPage,
});

const commissionsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/commissions',
  component: CommissionsIndexPage,
});

const commissionCalculatorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/commissions/calculator',
  component: CommissionCalculatorPage,
});

const commissionPlansRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/commissions/plans',
  component: CommissionPlansPage,
});

const teamIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/team',
  component: TeamIndexPage,
});

const teamMemberRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/team/$memberId',
  component: TeamMemberPage,
});

const teamInviteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/team/invite',
  component: TeamInvitePage,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsIndexPage,
});

const settingsPipelineRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/pipeline',
  component: SettingsPipelinePage,
});

const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/integrations',
  component: SettingsIntegrationsPage,
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/billing',
  component: SettingsBillingPage,
});

const reportsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports',
  component: ReportsIndexPage,
});

const leaderboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports/leaderboard',
  component: LeaderboardPage,
});

// Build route tree
const routeTree = rootRoute.addChildren([
  authedRoute.addChildren([
    dashboardRoute,
    pipelineRoute,
    dealsIndexRoute,
    newDealRoute,
    dealDetailRoute,
    commissionsIndexRoute,
    commissionCalculatorRoute,
    commissionPlansRoute,
    teamIndexRoute,
    teamMemberRoute,
    teamInviteRoute,
    settingsIndexRoute,
    settingsPipelineRoute,
    settingsIntegrationsRoute,
    settingsBillingRoute,
    reportsIndexRoute,
    leaderboardRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
